import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Wallet, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import {
  PAYMENT_SCHEDULE_TYPES,
  PAYMENT_SCHEDULE_DEFAULT,
  PAYMENT_SCHEDULE_LABELS,
  PAYMENT_SCHEDULE_DESCRIPTIONS,
} from "@/lib/paymentSchedule";
import PaymentPlanBreakdown from "./PaymentPlanBreakdown";

// =====================================================================
// SetBillingModelDialog — pick or change a Tx Plan's billing model.
// =====================================================================
// Calls public.set_plan_billing_model(plan_id, preset, dp_centavos).
// Shown from BillingStateCard's "Set billing model" button.
//
// Single source of truth: the options shown are exactly the billing
// models the clinic enables in Settings (`enabledPresets`), pre-selecting
// the clinic default (`defaultPreset`). Labels/descriptions come from
// lib/paymentSchedule so Settings, checkout, and the PDF never drift.
// Keys are the canonical payment_preset vocabulary
// (per_phase / 50_50 / full / custom).
//
// The RPC enforces: cannot change after Visit 1 charges are written.
// =====================================================================

function formatPHP(centavos) {
  if (!Number.isFinite(centavos)) return "—";
  const pesos = Math.round(centavos / 100);
  return `₱${pesos.toLocaleString()}`;
}

// On-the-go bundles have no fixed "last appointment" — they close when the
// front desk taps "Finish this treatment". Reword the models that reference a
// final appointment so bundle copy matches how a bundle actually settles.
// Formal Tx Plans keep the single-source-of-truth wording from paymentSchedule.
const BUNDLE_BLURBS = {
  full:    "Whole treatment paid up front, on this visit.",
  "50_50": "Half now, the rest when you finish the treatment.",
  custom:  "A down payment now, the rest settled when you finish.",
};

export default function SetBillingModelDialog({
  open,
  onOpenChange,
  planId,
  planTotalCentavos,    // bigint — used to gate the DP picker
  planVisitCount,       // int — number of visits/phases, for the breakdown preview
  currentPreset,        // 'full' | '50_50' | 'per_phase' | 'custom' | null
  currentDpCentavos,    // bigint | null
  currentMinPerVisitCentavos, // bigint | null — Flexible per-visit soft floor
  currentNumPayments,   // int | null — Flexible installments for balance-after-DP
  enabledPresets,       // string[] — clinic's Settings-enabled models (defaults to all)
  defaultPreset,        // string — clinic's Settings default (defaults to PAYMENT_SCHEDULE_DEFAULT)
  planIsBundle = false, // on-the-go bundle → plain "at Finish" wording, no "last appointment"
  demoMode = false,
  onSaved,              // (result) => void after success
}) {
  // Options shown = the clinic's enabled models, in canonical order,
  // labelled from the single source of truth.
  const options = useMemo(() => {
    const enabled = Array.isArray(enabledPresets) && enabledPresets.length > 0
      ? enabledPresets
      : PAYMENT_SCHEDULE_TYPES;
    return PAYMENT_SCHEDULE_TYPES
      .filter((k) => enabled.includes(k))
      .map((k) => ({
        key: k,
        label: PAYMENT_SCHEDULE_LABELS[k],
        blurb: (planIsBundle && BUNDLE_BLURBS[k]) || PAYMENT_SCHEDULE_DESCRIPTIONS[k],
      }));
  }, [enabledPresets, planIsBundle]);

  // The pre-selected model: an already-set preset wins; otherwise the
  // clinic default (if it's enabled), else the first enabled option.
  const resolvedDefault = useMemo(() => {
    const keys = options.map((o) => o.key);
    if (defaultPreset && keys.includes(defaultPreset)) return defaultPreset;
    if (keys.includes(PAYMENT_SCHEDULE_DEFAULT)) return PAYMENT_SCHEDULE_DEFAULT;
    return keys[0] || PAYMENT_SCHEDULE_DEFAULT;
  }, [options, defaultPreset]);

  const [preset, setPreset]   = useState(currentPreset || resolvedDefault);
  const [dpMode, setDpMode]   = useState("amount");  // 'amount' | 'percent'
  const [dpAmountPesos, setDpAmountPesos] = useState(
    currentDpCentavos != null ? String(Math.round(currentDpCentavos / 100)) : "",
  );
  const [dpPercent, setDpPercent] = useState(50);
  const [submitting, setSubmitting] = useState(false);
  // Flexible "More options" — a per-visit soft floor + a declared
  // pay-in-N-visits horizon. Collapsed unless one is already set, so the
  // everyday Flexible screen stays a two-tap decision.
  const [showMore, setShowMore] = useState(
    !!(currentMinPerVisitCentavos || currentNumPayments),
  );
  const [minPerVisitPesos, setMinPerVisitPesos] = useState(
    currentMinPerVisitCentavos != null && currentMinPerVisitCentavos > 0
      ? String(Math.round(currentMinPerVisitCentavos / 100)) : "",
  );
  const [numPayments, setNumPayments] = useState(
    currentNumPayments != null ? String(currentNumPayments) : "",
  );

  useEffect(() => {
    if (!open) return;
    setPreset(currentPreset || resolvedDefault);
    setDpMode("amount");
    setDpAmountPesos(currentDpCentavos != null ? String(Math.round(currentDpCentavos / 100)) : "");
    setDpPercent(50);
    setSubmitting(false);
    setShowMore(!!(currentMinPerVisitCentavos || currentNumPayments));
    setMinPerVisitPesos(
      currentMinPerVisitCentavos != null && currentMinPerVisitCentavos > 0
        ? String(Math.round(currentMinPerVisitCentavos / 100)) : "",
    );
    setNumPayments(currentNumPayments != null ? String(currentNumPayments) : "");
  }, [open, currentPreset, currentDpCentavos, currentMinPerVisitCentavos, currentNumPayments, resolvedDefault]);

  const totalPesos = Math.round(Number(planTotalCentavos || 0) / 100);

  // Resolve the DP centavos value the user is about to commit, given
  // their picker mode. Only relevant for preset='custom'.
  const dpCentavos = useMemo(() => {
    if (preset !== "custom") return null;
    if (dpMode === "percent") {
      return Math.round(Number(planTotalCentavos || 0) * (Number(dpPercent) / 100));
    }
    const pesos = Number(dpAmountPesos);
    if (!Number.isFinite(pesos) || pesos < 0) return 0;
    return pesos * 100;
  }, [preset, dpMode, dpPercent, dpAmountPesos, planTotalCentavos]);

  const dpExceedsTotal = preset === "custom" && dpCentavos > Number(planTotalCentavos || 0);

  // Flexible "More options" — resolve the two knobs to their committed
  // values (null = not configured, so the DB leaves the key absent).
  const minPerVisitCentavos = useMemo(() => {
    if (preset !== "custom") return null;
    const pesos = Number(minPerVisitPesos);
    if (!Number.isFinite(pesos) || pesos <= 0) return null;
    return Math.round(pesos * 100);
  }, [preset, minPerVisitPesos]);

  const numPaymentsInt = useMemo(() => {
    if (preset !== "custom") return null;
    const n = parseInt(numPayments, 10);
    if (!Number.isFinite(n) || n < 1) return null;
    return n;
  }, [preset, numPayments]);

  const close = () => { if (!submitting) onOpenChange(false); };

  async function save() {
    if (submitting) return;
    if (preset === "custom" && (dpCentavos == null || dpCentavos < 0)) {
      toast.error("Pick a down payment first.");
      return;
    }
    if (dpExceedsTotal) {
      toast.error("Down payment can't exceed the plan total.");
      return;
    }
    const customSchedule = preset === "custom"
      ? {
          dp_centavos: dpCentavos,
          ...(minPerVisitCentavos != null ? { min_per_visit_centavos: minPerVisitCentavos } : {}),
          ...(numPaymentsInt != null ? { num_payments: numPaymentsInt } : {}),
        }
      : null;
    if (demoMode) {
      const data = {
        preset,
        custom_schedule: customSchedule,
        dp_centavos: preset === "custom" ? dpCentavos : null,
      };
      toast.info("Tutorial only. No billing model was saved.");
      onSaved?.(data);
      onOpenChange(false);
      return;
    }
    setSubmitting(true);
    const { data, error } = await supabase.rpc("set_plan_billing_model", {
      p_plan_id:     planId,
      p_preset:      preset,
      p_dp_centavos: preset === "custom" ? dpCentavos : null,
      p_min_per_visit_centavos: preset === "custom" ? minPerVisitCentavos : null,
      p_num_payments:           preset === "custom" ? numPaymentsInt : null,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message ?? "Couldn't save billing model.");
      return;
    }
    toast.success("Billing model saved.");
    onSaved?.(data);
    onOpenChange(false);
  }

  if (!open) return null;

  return (
    <div
      role="dialog" aria-modal="true"
      data-testid="set-billing-model-dialog"
      data-tutorial-id="set-billing-model-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Wallet className="w-4 h-4 text-teal-600" strokeWidth={2.25} />
              Set billing model
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Plan total: <span className="font-semibold text-slate-800 tabular-nums">{formatPHP(planTotalCentavos)}</span>
            </p>
          </div>
          <button type="button" onClick={close} disabled={submitting}
            className="p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-40">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          <div className="space-y-2">
            {options.map((p) => {
              const checked = preset === p.key;
              const tutorialId = p.key === "per_phase"
                ? "set-billing-preset-per-phase"
                : `set-billing-preset-${p.key.replace(/_/g, "-")}`;
              return (
                <label
                  key={p.key}
                  data-testid={`set-billing-preset-${p.key}`}
                  data-tutorial-id={tutorialId}
                  className={[
                    "flex items-start gap-3 px-3 py-2.5 rounded-lg border cursor-pointer",
                    checked ? "border-teal-500 bg-teal-50" : "border-slate-200 hover:bg-slate-50",
                  ].join(" ")}
                >
                  <input
                    type="radio"
                    name="billing-preset"
                    checked={checked}
                    onChange={() => setPreset(p.key)}
                    className="mt-1 accent-teal-600"
                  />
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-slate-900">{p.label}</div>
                    <div className="text-[11px] text-slate-600 mt-0.5">{p.blurb}</div>
                  </div>
                </label>
              );
            })}
          </div>

          {preset === "custom" ? (
            <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="set-billing-custom-panel">
              <div className="text-[12px] font-semibold text-slate-800">Down payment</div>
              {/* Quick-pick down-payment tabs — Waive (₱0) + common percentages.
                  Each sets percent mode; "Other" below is the free amount/percent input.
                  No 50% here on purpose: a 50% split is the separate "Deposit +
                  balance (50/50)" model, not a Flexible down payment. */}
              <div className="flex flex-wrap gap-1.5" data-testid="set-billing-dp-quickpick">
                {[{ label: "Waive", v: 0 }, { label: "20%", v: 20 }, { label: "30%", v: 30 }].map((q) => {
                  const active = dpMode === "percent" && Number(dpPercent) === q.v;
                  return (
                    <button
                      key={q.label}
                      type="button"
                      onClick={() => { setDpMode("percent"); setDpPercent(q.v); }}
                      data-testid={`set-billing-dp-quick-${q.v}`}
                      className={[
                        "px-2.5 py-1 rounded-md text-[12px] font-semibold border",
                        active ? "border-teal-500 bg-teal-50 text-teal-700" : "border-slate-200 text-slate-600 hover:bg-slate-50",
                      ].join(" ")}
                    >
                      {q.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-3 text-[12px]">
                <span className="text-slate-500">Other:</span>
                <label className="inline-flex items-center gap-1.5">
                  <input type="radio" checked={dpMode === "amount"} onChange={() => setDpMode("amount")} className="accent-teal-600" />
                  Amount (₱)
                </label>
                <label className="inline-flex items-center gap-1.5">
                  <input type="radio" checked={dpMode === "percent"} onChange={() => setDpMode("percent")} className="accent-teal-600" />
                  Percent of total
                </label>
              </div>
              {dpMode === "amount" ? (
                <div>
                  <input
                    type="number" min={0} max={totalPesos}
                    value={dpAmountPesos}
                    onChange={(e) => setDpAmountPesos(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder={String(Math.round(totalPesos / 2))}
                    data-testid="set-billing-dp-amount"
                    className="w-full px-2 py-1.5 text-sm rounded border border-slate-300 focus:border-teal-500 outline-none tabular-nums"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Remaining after DP: <strong className="tabular-nums">{formatPHP(Number(planTotalCentavos || 0) - (Number(dpAmountPesos || 0) * 100))}</strong>
                  </p>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-3">
                    <input
                      type="range" min={0} max={100} step={5}
                      value={dpPercent}
                      onChange={(e) => setDpPercent(Number(e.target.value))}
                      data-testid="set-billing-dp-percent"
                      className="flex-1 accent-teal-600"
                    />
                    <span className="text-[12px] font-semibold tabular-nums w-12 text-right">{dpPercent}%</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    DP: <strong className="tabular-nums">{formatPHP(dpCentavos)}</strong>
                    {" · "}
                    Remaining: <strong className="tabular-nums">{formatPHP(Number(planTotalCentavos || 0) - dpCentavos)}</strong>
                  </p>
                </div>
              )}
              {dpExceedsTotal ? (
                <div className="text-[11px] text-rose-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Down payment can't be more than the plan total.
                </div>
              ) : null}

              {/* More options — the two power knobs, tucked away so the
                  everyday Flexible screen stays a simple down-payment
                  decision. Only the clinics that want a per-visit floor or
                  a fixed visit horizon ever open this. */}
              <div className="border-t border-slate-200 pt-2.5">
                <button
                  type="button"
                  onClick={() => setShowMore((v) => !v)}
                  data-testid="set-billing-more-options"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900"
                >
                  {showMore ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  More options
                </button>
                {showMore ? (
                  <div className="mt-2 space-y-3" data-testid="set-billing-more-panel">
                    <div>
                      <label className="block text-[12px] font-semibold text-slate-800">
                        Collect at least, each visit <span className="font-normal text-slate-400">(optional)</span>
                      </label>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[13px] text-slate-400">₱</span>
                        <input
                          type="number" min={0} inputMode="numeric"
                          value={minPerVisitPesos}
                          onChange={(e) => setMinPerVisitPesos(e.target.value.replace(/[^0-9]/g, ""))}
                          placeholder="0"
                          data-testid="set-billing-min-per-visit"
                          className="w-32 px-2 py-1.5 text-sm rounded border border-slate-300 focus:border-teal-500 outline-none tabular-nums"
                        />
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Suggested each visit — staff can still collect less if the patient is short.
                      </p>
                    </div>
                    <div>
                      <label className="block text-[12px] font-semibold text-slate-800">
                        Number of payments <span className="font-normal text-slate-400">(optional)</span>
                      </label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="number" min={1} inputMode="numeric"
                          value={numPayments}
                          onChange={(e) => setNumPayments(e.target.value.replace(/[^0-9]/g, ""))}
                          placeholder="—"
                          data-testid="set-billing-num-payments"
                          className="w-20 px-2 py-1.5 text-sm rounded border border-slate-300 focus:border-teal-500 outline-none tabular-nums"
                        />
                        <span className="text-[11px] text-slate-500">
                          payments — splits the balance after the down payment evenly. Good for ongoing treatment.
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          <PaymentPlanBreakdown
            preset={preset}
            totalCentavos={Number(planTotalCentavos || 0)}
            visitCount={planVisitCount}
            dpCentavos={dpCentavos}
            minPerVisitCentavos={minPerVisitCentavos}
            numPayments={numPaymentsInt}
            data-testid="set-billing-breakdown-preview"
          />
        </div>

        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center gap-3 shrink-0">
          <button type="button" onClick={close} disabled={submitting}
            className="ml-auto text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded">
            Cancel
          </button>
          <button type="button" onClick={save}
            disabled={submitting || dpExceedsTotal}
            data-testid="set-billing-save"
            data-tutorial-id="set-billing-save"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded disabled:bg-slate-300 disabled:cursor-not-allowed">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wallet className="w-3.5 h-3.5" />}
            Save billing model
          </button>
        </div>
      </div>
    </div>
  );
}
